/**
 * Authenticated Fetch Interceptor
 * Wraps fetch with automatic cookie-based authentication handling.
 * Implements request queuing during re-authentication.
 * With httpOnly cookies, the browser automatically sends credentials.
 * @packageDocumentation
 */

import { useAuthStore } from '@/stores/auth';
import { useEnvironmentStore } from '@/stores';
import {
  rejectQueue,
  queueRequest,
  processQueue,
  isRefreshingSession,
  setRefreshingSession,
} from '@/lib/request-queue';

/**
 * Performs a fetch request with automatic cookie-based auth handling.
 * - Includes credentials (cookies) automatically
 * - Sets session expired flag on 401 SESSION_EXPIRED
 * - Queues requests during re-authentication
 * - Sets LDA unavailable flag on 503
 *
 * @param { string } url Request URL.
 * @param { RequestInit } options Fetch options.
 * @returns { Promise<Response> } Fetch response.
 */
export const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
  });

  if (response.status === 401) {
    const errorData = await response
      .clone()
      .json()
      .catch(() => ({}));
    const errorCode = (errorData as { error?: string }).error;

    if (errorCode === 'SESSION_EXPIRED') {
      if (isRefreshingSession()) {
        return queueRequest(url, options);
      }

      setRefreshingSession(true);
      useAuthStore.getState().markSessionExpired();

      // Add timeout to prevent memory leaks if re-auth never completes
      const REAUTH_TIMEOUT_MS = 2 * 60 * 1000;

      return new Promise((resolve, reject) => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let unsubscribeFn: (() => void) | null = null;

        const cleanup = (): void => {
          if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          if (unsubscribeFn !== null) {
            unsubscribeFn();
            unsubscribeFn = null;
          }
        };

        unsubscribeFn = useAuthStore.subscribe((state, prevState) => {
          if (prevState.sessionExpired && !state.sessionExpired) {
            cleanup();

            if (state.isAuthenticated) {
              processQueue();
              fetch(url, { ...options, credentials: 'include' })
                .then(resolve)
                .catch(reject);
            } else {
              const error = new Error('Re-authentication failed');
              rejectQueue(error);
              reject(error);
            }
          }
        });

        // Safety timeout to prevent memory leaks if re-auth never completes
        timeoutId = setTimeout(() => {
          // Timeout reached - clean up and reject all queued requests
          cleanup();

          // Reset session expired state to allow user to attempt re-auth again
          setRefreshingSession(false);

          // Reject all queued requests with a timeout error
          const error = new Error('Re-authentication timeout after 2 minutes');
          rejectQueue(error);
          reject(error);
        }, REAUTH_TIMEOUT_MS);
      });
    }

    throw new Error('Unauthorized');
  }

  if (response.status === 503) {
    useAuthStore.getState().setLdaUnavailable(true);

    // LDA service unavailable - handle accordingly
    throw new Error('Authentication service unavailable');
  }

  return response;
};

/**
 * Performs a JSON fetch request with automatic auth handling.
 * Convenience wrapper that sets Content-Type and parses JSON response.
 *
 * @param { string } url Request URL.
 * @param { RequestInit } options Fetch options.
 * @returns { Promise<T> } Parsed JSON response.
 */
export const fetchJsonWithAuth = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const headers = new Headers(options.headers);

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (!headers.has('X-Requested-With')) {
    headers.set('X-Requested-With', 'XMLHttpRequest');
  }

  // Inject retailer/environment headers (same as tRPC client)
  const { currentEnvironment, currentRetailerCode } = useEnvironmentStore.getState();

  if (currentRetailerCode && !headers.has('X-Retailer-ID')) {
    headers.set('X-Retailer-ID', currentRetailerCode);
  }

  if (currentEnvironment && !headers.has('X-Environment')) {
    headers.set('X-Environment', currentEnvironment);
  }

  const response = await fetchWithAuth(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({
      error: {
        code: 'UNKNOWN',
        message: response.statusText,
      },
    }));

    throw new Error(errorBody.error?.message ?? response.statusText);
  }

  return response.json() as Promise<T>;
};
